/**
 * Tiny client-side helper to inform the backend audit log every time a
 * Google API call is fired from the browser, and to check whether a
 * capability is currently enabled by admin toggle.
 *
 * Usage:
 *   import { isGoogleEnabled, trackGoogle } from '../../lib/googleApiTrack';
 *   const ok = await isGoogleEnabled('geocode_reverse');
 *   if (!ok) return null;
 *   // ... fire the actual fetch to maps.googleapis.com ...
 *   trackGoogle('geocode_reverse', 'staff');
 *
 * Failures are silent — tracking must NEVER break a feature.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cache the inventory for 60 s to avoid hammering the backend on every
// component mount. The cache is invalidated when the page reloads.
let _cache = null;
let _cacheAt = 0;
const CACHE_MS = 60 * 1000;

async function _getInventory() {
  const now = Date.now();
  if (_cache && now - _cacheAt < CACHE_MS) return _cache;
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const r = await fetch(`${API_URL}/api/admin/google-api/inventory`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return null;
    const data = await r.json();
    const map = {};
    (data.items || []).forEach((it) => { map[it.key] = it; });
    _cache = map;
    _cacheAt = now;
    return map;
  } catch { return null; }
}

export async function isGoogleEnabled(key) {
  // Default to TRUE so missing auth or first-load latency never blocks
  // legitimate features — backend toggle is still the source of truth
  // for backend endpoints (snap_to_roads, directions).
  const inv = await _getInventory();
  if (!inv) return true;
  const item = inv[key];
  if (!item) return true;
  return item.enabled !== false;
}

export function trackGoogle(key, module = null, success = true, count = 1) {
  // Fire-and-forget. We don't await the response.
  try {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_URL}/api/admin/google-api/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ key, module, success, count }),
      keepalive: true,
    }).catch(() => {});
  } catch {}
}

export function clearGoogleApiCache() {
  _cache = null;
  _cacheAt = 0;
}
